import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetMarketShopProducts } from '@/api/market-shop/products/get-market-shop-products'
import { useIntersectionSentinel } from '@/hooks'
import { EmptyState } from '@/pages/market-shop/empty-state'
import { useMarketShopStore } from '@/pages/market-shop/market-shop.store'

import { ProductCard } from './product-card'
import * as S from './products-container.styled'

export type ProductsContainerProps = {
  useGetMarketShopProductsResult: ReturnType<typeof useGetMarketShopProducts>
}

export const ProductsContainer = ({ useGetMarketShopProductsResult }: ProductsContainerProps) => {
  const { t } = useTranslation(['market-shop'])
  const { data: products, isFetching, fetchNextPage, hasNextPage } = useGetMarketShopProductsResult
  const flatProducts = useMemo(() => {
    return products?.pages.flatMap((p) => p.products) ?? []
  }, [products])
  const flatProductsAmount = flatProducts.length

  const marketShopStore = useMarketShopStore()

  const { rootRef, sentinelRef } = useIntersectionSentinel<HTMLDivElement, HTMLDivElement>(
    () => {
      if (!hasNextPage) return
      void fetchNextPage()
    },
    { deps: [hasNextPage, flatProductsAmount] },
  )

  return (
    <S.ProductsContainer ref={rootRef} $hasProductsInCart={marketShopStore.hasProductsInCart()}>
      {!isFetching && !flatProductsAmount && (
        <EmptyState
          title={t('market-shop:productsContainer.emptyTitle')}
          message={t('market-shop:productsContainer.emptyDescription')}
        />
      )}
      <S.ProductsWrapper $hasProductsToShow={!!flatProductsAmount}>
        {flatProducts.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            quantity={marketShopStore.getQuantity(product)}
            hideControls={!marketShopStore.isOpened}
            onAddToCart={marketShopStore.addProduct}
            onSubtractFromCart={marketShopStore.subtractProduct}
          />
        ))}
        <div ref={sentinelRef} />
      </S.ProductsWrapper>
      {isFetching && <S.Loader size="small" $hasContent={!!flatProductsAmount} />}
    </S.ProductsContainer>
  )
}
